// DealsWidget is a singleton component - there should be only one on the page.

import Component from "../../lib/component";
import ComponentEvent from "../../lib/component_event";
import { fireElementEvent } from "../../helpers/event";
import { injectStylesheet } from "../../helpers/injection";
import { XHRFetcher } from "../../lib/xhr_fetcher";

import { DealsWidgetConfig } from "./config";
import DealsWidgetBuilder from "./builder";
import DealsWidgetPanel from "./components/deals_widget_panel";
import DealsWidgetSpinner from "./components/deals_widget_spinner";
import TypeWriter from "../../helpers/type_writer";
import stateMachine from "./instances/deals_widget_state_machine";
import { initFromPersistentStore } from "../../lib/mixins/persistable";

export default class DealsWidget extends Component {
  static get config() {
    return DealsWidgetConfig;
  }

  constructor(id_or_element) {
    // Initialize persistent storage and check dismissal expiration
    initFromPersistentStore(stateMachine);

    // Check if dismissal has expired (2 days)
    const TWO_DAYS_MS = 2 * 24 * 60 * 60 * 1000;
    const dismissedAt = stateMachine.state.dismissedAt;
    if (stateMachine.state.dismissed) {
      if (!dismissedAt || Date.now() - dismissedAt > TWO_DAYS_MS) {
        stateMachine.commit("dismissed", false);
        stateMachine.commit("dismissedAt", null);
      }
    }

    // Reset transient state for new widget instances (preserves dismissed state)
    // This is needed because the state machine is a singleton shared across AJAX navigations
    stateMachine.commit("isOpen", false);
    stateMachine.commit("isLoading", false);
    stateMachine.commit("isError", false);
    stateMachine.commit("hasData", false);
    stateMachine.commit("errorMessage", "");

    super({
      element: id_or_element,
      config: DealsWidget.config,
      stateMachine, // Share state machine with panel sub-component
    });

    // Build the DOM structure (trigger + backdrop only, panel is sub-component)
    this.element = new DealsWidgetBuilder(this.element).getElement();

    this.setDatasetConfig(DealsWidget.config.datasetConfigKey);
    this.configUrl = this.getDatasetConfigValue(DealsWidget.config.configUrlDatasetConfigKey);
    this.dealsBaseUrl = this.getDatasetConfigValue(DealsWidget.config.dealsBaseUrlDatasetConfigKey);
    this.stylesheetPath = this.getDatasetConfigValue(DealsWidget.config.stylesheetPathDatasetConfigKey);
    this.initialDealCount = this.getDatasetConfigValue(DealsWidget.config.initialDealCountDatasetConfigKey) || 0;

    // Get elements (trigger, dismisser, and backdrop)
    this.triggerElement = this.element.querySelector(".deals_widget__trigger");
    this.dismisserElement = this.element.querySelector(".deals_widget__dismisser");
    this.dismisserButtonElement = this.element.querySelector(".deals_widget__dismisser_button");
    this.backdropElement = this.element.querySelector(".deals_widget__backdrop");
    this.titleElement = this.element.querySelector(".deals_widget__trigger_title");

    // Create sub-components (share state machine via base class). Adopt them so the base
    // destroy() cascade tears them down with this widget.
    this.panel = this.adoptChildComponents(new DealsWidgetPanel(this));
    this.spinner = this.adoptChildComponents(new DealsWidgetSpinner(this));

    // Local state (not in state machine)
    this.hasConfig = false;
    this.totalDealsCount = this.initialDealCount;
    this.promocode = "";

    // Bind event handlers
    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleDismisserClick = this.handleDismisserClick.bind(this);
    this.handleBackdropClick = this.handleBackdropClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);

    // Attach event listeners. The trigger/dismisser/backdrop listeners are on this widget's own
    // descendant elements, so they are discarded with the element and need no teardown. Only the
    // document listener outlives the element — register its removal, and the body scroll-lock
    // class in case the widget is destroyed while open.
    this.triggerElement.addEventListener("click", this.handleTriggerClick);
    this.dismisserButtonElement.addEventListener("click", this.handleDismisserClick);
    this.backdropElement.addEventListener("click", this.handleBackdropClick);
    document.addEventListener("keydown", this.handleKeyDown);
    this.onDestroy(() => document.removeEventListener("keydown", this.handleKeyDown));
    this.onDestroy(() => document.body.classList.remove("body--deals_widget_open"));

    // Fetch config first - widget only shows on successful config fetch (network check)
    if (this.stylesheetPath) {
      injectStylesheet(this.stylesheetPath, () => {
        this.fetchConfig();
      });
    } else {
      this.fetchConfig();
    }
  }

  renderInitial() {
    this.element.style.display = "";
    // Force a reflow before adding visible class to ensure transition works
    this.element.offsetHeight;
    this.addHtmlClassState("visible");
  }

  // Fetch widget config (count, promocode) - also serves as network connectivity check
  fetchConfig() {
    if (!this.configUrl) {
      console.warn("[DealsWidget] No config URL provided, widget hidden");
      return;
    }
    console.log("[DealsWidget] Fetching config from:", this.configUrl);
    const fetcher = new XHRFetcher(this.configUrl);

    fetcher
      .fetch()
      .then((response) => {
        console.log("[DealsWidget] Config response received, length:", response?.length);
        const config = JSON.parse(response);
        this.hasConfig = true;
        this.totalDealsCount = config.count || 0;
        this.promocode = config.promocode || "RANKERS";
        console.log("[DealsWidget] Config parsed:", { count: this.totalDealsCount, promocode: this.promocode });

        // Update promocode text
        this.updatePromocode();

        // Now show the widget
        this.renderInitial();
        this.render();
      })
      .catch((error) => {
        // Network error - keep widget hidden (no renderInitial call)
        console.warn("[DealsWidget] Config fetch failed, widget hidden:", error?.name, error?.message, error);
      });
  }

  updatePromocode() {
    const promoContainer = this.element.querySelector(".deals_widget__trigger_promo");
    if (promoContainer && this.promocode) {
      // Create promocode element with config - component system will auto-init
      const promocodeEl = document.createElement("div");
      promocodeEl.classList.add("promocode");
      promocodeEl.dataset.promocode__config = JSON.stringify({
        code: this.promocode,
        animate: true,
        copyable: false,
        promo_text: [`${this.totalDealsCount} Activity Deals`, "Save 10-15%"]
      });
      promocodeEl.style.display = "none"; // Promocode component shows on init
      promoContainer.appendChild(promocodeEl);
    }
  }

  // Build deals URL using configured base URL and current page path
  buildApiUrl() {
    let pagePath = window.location.pathname.replace(/^\//, "");
    if (!pagePath) {
      pagePath = "home";
    }
    return `${this.dealsBaseUrl}${pagePath}.snip`;
  }

  fetchDeals() {
    const isLoading = this.readStateMachineValue("isLoading");
    const hasData = this.readStateMachineValue("hasData");

    if (isLoading || hasData) return;

    if (!this.dealsBaseUrl) {
      console.warn("[DealsWidget] No deals base URL provided");
      return;
    }

    this.dispatchAction("startLoading");

    const fetcher = new XHRFetcher(this.buildApiUrl());

    fetcher
      .fetch()
      .then((html) => {
        this.dispatchAction("loadSuccess");

        // Set content on panel
        this.panel.setContent(html);

        // Extract total count and header text from data attributes
        const experiencesEl = this.panel.contentElement.querySelector(".deals_widget__experiences");
        if (experiencesEl) {
          this.totalDealsCount = parseInt(experiencesEl.dataset.totalCount, 10) || 0;
          const headerText = experiencesEl.dataset.headerText;
          if (headerText && this.titleElement) {
            this.titleTypeWriter = new TypeWriter(this.titleElement, {
              text: headerText,
              delay: 70,
            });
            this.titleTypeWriter.start();
          }
        }

        this.fireWidgetEvent(DealsWidget.config.dataLoadedEventName);
      })
      .catch((error) => {
        this.dispatchAction("loadError", "Unable to load deals. Please try again.");
        console.error("[DealsWidget] Error fetching deals:", error);
        this.fireWidgetEvent(DealsWidget.config.dataErrorEventName);
      });
  }

  open() {
    this.dispatchAction("open");
    document.body.classList.add("body--deals_widget_open");
    this.fetchDeals();
    this.fireWidgetEvent(DealsWidget.config.openEventName);
  }

  close() {
    this.dispatchAction("close");
    document.body.classList.remove("body--deals_widget_open");
    this.fireWidgetEvent(DealsWidget.config.closeEventName);
  }

  toggle() {
    const isOpen = this.readStateMachineValue("isOpen");
    if (isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  retry() {
    this.dispatchAction("retry");
    this.fetchDeals();
  }

  handleTriggerClick(event) {
    event.preventDefault();
    this.toggle();
  }

  handleDismisserClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const isDismissed = this.readStateMachineValue("dismissed");
    if (isDismissed) {
      // Un-dismiss - show the widget again
      this.dispatchAction("undismiss");
    } else {
      // Dismiss - close first if open, then transition to tab state
      const isOpen = this.readStateMachineValue("isOpen");
      if (isOpen) {
        this.close();
      }
      this.dispatchAction("dismiss");
    }
  }

  handleBackdropClick(event) {
    event.preventDefault();
    this.close();
  }

  handleKeyDown(event) {
    const isOpen = this.readStateMachineValue("isOpen");
    if (event.key === "Escape" && isOpen) {
      this.close();
    }
  }

  fireWidgetEvent(eventName) {
    const componentEvent = new ComponentEvent(eventName, { target: this });
    this.fireEvent(componentEvent);
    fireElementEvent(this.element, eventName);
  }

  render() {
    const isOpen = this.readStateMachineValue("isOpen");
    const isLoading = this.readStateMachineValue("isLoading");
    const isError = this.readStateMachineValue("isError");
    const hasData = this.readStateMachineValue("hasData");

    // Update state classes on root element
    const isDismissed = this.readStateMachineValue("dismissed");
    const isOpening = isOpen && !(hasData || isError);
    const isFullyOpen = isOpen && (hasData || isError);

    this.conditionallySetHtmlClassState(isDismissed, "dismissed");
    this.conditionallySetHtmlClassState(!isOpen && !isDismissed, "closed");
    this.conditionallySetHtmlClassState(isOpening, "opening");
    this.conditionallySetHtmlClassState(isFullyOpen, "open");
    this.conditionallySetHtmlClassState(isLoading, "loading");
    this.conditionallySetHtmlClassState(isError, "error");
    this.conditionallySetHtmlClassState(hasData, "has_data");

    // Failsafe: ensure body scroll lock is synced with isOpen state
    // This catches edge cases where state and body class get out of sync
    if (isOpen) {
      document.body.classList.add("body--deals_widget_open");
    } else {
      document.body.classList.remove("body--deals_widget_open");
    }

    // Panel renders itself via shared state machine
  }

  // No destroy() override — the document listener and body class are registered via onDestroy(),
  // and the panel/spinner are adopted, so the base Component#destroy() (via the reaper) tears
  // everything down and deregisters this widget. This also fixes the old override's dismissed
  // early-out, which skipped base teardown entirely.
}
